import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GRAY} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';

import {getBenchlingAppConfigurationQuery} from './__generated/getBenchlingAppConfigurationQuery';
import BenchlingAppConfigurationRowSet from './BenchlingAppConfigurationRowSet';
import {initializeConfigValidation} from './BenchlingAppConfigurationSection';
import {UpdateArrayElementValidationCallback} from './validation/ValidationTypes';

type ArrayElement = NonNullable<
  ExtractByTypename<
    getBenchlingAppConfigurationQuery['benchlingApp'],
    'BenchlingAppNode'
  >['configuration'][number]['elements']
>[number];

type ArrayElementConfig = ArrayElement['elementConfigs'][number];

export interface Props {
  arrayName: string;
  element: ArrayElement;
  forceExpand: boolean;
  changeElement: (newElement: ArrayElement) => void;
  removeElement: () => void;
  updateArrayElementValidation: UpdateArrayElementValidationCallback;
}

export default function BenchlingAppConfigurationArrayElement({
  arrayName,
  element,
  forceExpand,
  changeElement,
  removeElement,
  updateArrayElementValidation,
}: Props): JSX.Element {
  const [expanded, updateExpanded] = useState(forceExpand);
  const [configsValidation, updateConfigsValidation] = useState(
    initializeConfigValidation(element.elementConfigs)
  );

  useEffect(() => {
    updateArrayElementValidation(arrayName + element.name, {
      validation: configsValidation,
      arrayElementName: element.name,
      arrayName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configsValidation, arrayName, element.name]);

  function toggleExpanded(): void {
    updateExpanded(!expanded);
  }

  function handleChangeElementConfigs(newElementConfigs: Array<ArrayElementConfig>): void {
    const newElement: ArrayElement = {
      ...element,
      elementConfigs: newElementConfigs,
    };
    changeElement(newElement);
  }

  return (
    <div className={css(styles.rowContainer)}>
      <div className="u-lr-split">
        <span>
          <span className={css(styles.cursorHover)} onClick={toggleExpanded}>
            {expanded ? (
              <BenchlingIcon className="u-small-spacing-right" icon="chevron-down" />
            ) : (
              <BenchlingIcon className="u-small-spacing-right" icon="chevron-right" />
            )}
          </span>
          <span className={css(styles.elementName)}>
            <EditableTextField
              emptyValueAllowed={false}
              requestChange={(newElementName: string) => {
                const oldElementName = element.name;
                const newElement: ArrayElement = {...element, name: newElementName};
                changeElement(newElement);
                updateArrayElementValidation(arrayName + oldElementName, null);
              }}
              type={TextFieldType.TEXT}
              value={element.name}
              isFullWidth={false}
            />
          </span>
        </span>
        <span
          className={classNames('u-small-spacing-right', css(styles.cursorHover))}
          onClick={removeElement}
        >
          <BenchlingIcon icon="format-delete" />
        </span>
      </div>
      {expanded && element.elementConfigs && (
        <div className={css(styles.elementConfigContainer)}>
          <BenchlingAppConfigurationRowSet
            configuration={element.elementConfigs}
            updateConfiguration={handleChangeElementConfigs}
            configsValidation={configsValidation}
            updateConfigsValidation={updateConfigsValidation}
            updateArrayElementValidation={(arrayElementKey, newArrayElementValidation) => {}}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  rowContainer: {
    paddingLeft: '10px',
    paddingRight: '10px',
    border: '1px',
    borderStyle: 'solid',
    borderColor: GRAY,
    marginBottom: '10px',
  },
  elementConfigContainer: {
    padding: '15px',
    paddingTop: '0px',
  },
  description: {
    marginBottom: '8px',
  },
  cursorHover: {
    ':hover': {
      cursor: 'pointer',
    },
  },
  elementName: {
    display: 'inline-block',
    fontWeight: 'bold',
    marginBottom: '0px',
  },
});
